/**
 * The shape of each prop kind, keyed by `PropDef.mesh`.
 *
 * ===========================================================================
 * EVERYTHING HERE IS AUTHORED INSIDE THE UNIT CUBE
 * ===========================================================================
 *
 * Every geometry below spans -0.5..0.5 on all three axes and is centred on the
 * origin. That is not a style rule, it is the contract that makes the rest of
 * the renderer work: `scene.ts` poses props (and disguised players) with a
 * matrix whose scale is `halfWidth*2, height, halfDepth*2` straight from
 * `PropDef.hull`, so a geometry that fills the unit cube comes out filling its
 * hull exactly, at whatever size the catalogue says. Nothing here repeats a
 * prop's dimensions, which is why changing a hull in `props.ts` changes both
 * what you hit and what you see, with no second number to update.
 *
 * The corollary is that a mesh may only *inset* from the cube, never overflow
 * it. A barrel is a cylinder inscribed in its box, so its corners are empty and
 * a shot through one connects with nothing visible — that is a deliberate trade
 * for props that look like props, and F4 draws the hull over the model so the
 * discrepancy is one keypress away rather than something you discover by
 * missing. A mesh that overflowed would be the opposite and much worse: visible
 * geometry you cannot hit.
 *
 * Yaw is applied by the same matrix, and prop footprints are square by
 * validator rule, so a rotated prop's hull is unchanged. Anything asymmetric in
 * XZ (the pallet's slats, the chair's back) turns without lying about what it
 * collides with.
 *
 * ===========================================================================
 * WHY PROCEDURAL
 * ===========================================================================
 *
 * Same argument `textures.ts` makes at more length: there is no asset pipeline,
 * `GameRenderer` is constructed after the handshake names the level and draws
 * on the next frame, and anything asynchronous would mean a first frame with
 * props missing — the frame a player joins on. These are a few dozen triangles
 * each and are built once per kind per level.
 */

import * as THREE from "three";

/** Segments around a revolved prop. Cheap; these are small on screen. */
const RADIAL_SEGMENTS = 16;

/**
 * The geometry for one prop kind, or a plain unit cube for a kind that has not
 * been modelled.
 *
 * The fallback is the point rather than an oversight: adding an entry to
 * `PROP_DEFS` produces a prop that has not been art-directed yet, never an
 * invisible one, exactly as `materials.ts` does for a kind with no `Surface`.
 * Callers own the returned geometry and are responsible for disposing it —
 * `GameRenderer` keeps one per kind in `propAssets` and disposes it there.
 */
export function propGeometry(mesh: string): THREE.BufferGeometry {
  switch (mesh) {
    case "crate":
    case "crate_small":
      return crate();
    case "barrel":
      return barrel();
    case "cone":
      return cone();
    case "pallet":
      return pallet();
    case "plant":
      return plant();
    default:
      return new THREE.BoxGeometry(1, 1, 1);
  }
}

/**
 * A crate: the unit cube with its edges taken off.
 *
 * The bevel is 2% of the cube and exists entirely for the specular break along
 * each edge — a perfectly sharp box reads as untextured no matter what is on
 * it, because every edge is a hard discontinuity with no highlight running
 * down it. It insets rather than expands, so the mesh still fits its hull.
 */
function crate(): THREE.BufferGeometry {
  const box = new THREE.BoxGeometry(0.98, 0.98, 0.98, 1, 1, 1);
  // A crate is the prop players are most often disguised as, and the one they
  // stare at longest deciding whether it is a player. Keeping it a box is
  // deliberate: any silhouette difference from the crates around it is a tell.
  return box;
}

/**
 * A barrel: a cylinder inscribed in the cube, with a rolled rim top and bottom.
 *
 * The rims are the whole reason this is not one cylinder. A bare cylinder under
 * a metal texture reads as a pipe; the two slightly proud bands at the ends are
 * what make it read as a barrel at the distance you actually see one.
 */
function barrel(): THREE.BufferGeometry {
  const body = new THREE.CylinderGeometry(0.46, 0.46, 1, RADIAL_SEGMENTS, 1, false);
  const upper = new THREE.CylinderGeometry(0.5, 0.5, 0.08, RADIAL_SEGMENTS, 1, true);
  upper.translate(0, 0.34, 0);
  const lower = upper.clone();
  lower.translate(0, -0.68, 0);
  return merge([body, upper, lower]);
}

/**
 * A traffic cone: the cone itself on a square base.
 *
 * The base is what stops it looking like a party hat. It is 90% of the
 * footprint rather than 100% so the silhouette is a cone with a foot rather
 * than a cone in a box.
 */
function cone(): THREE.BufferGeometry {
  const body = new THREE.ConeGeometry(0.32, 0.86, RADIAL_SEGMENTS, 1, false);
  body.translate(0, 0.07, 0);
  const base = new THREE.BoxGeometry(0.9, 0.14, 0.9);
  base.translate(0, -0.43, 0);
  return merge([body, base]);
}

/**
 * A pallet stack: two decks of slats with blocks between them.
 *
 * Asymmetric in XZ on purpose — the slats run along X — so that yaw is visible
 * on a prop whose hull is square and therefore rotation-invariant. A hider who
 * turns a pallet changes what it looks like without changing what it collides
 * with, which is the good half of that trade.
 */
function pallet(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  // Two decks, five slats each, gaps between them.
  for (const y of [0.42, -0.14]) {
    for (let i = 0; i < 5; i++) {
      const slat = new THREE.BoxGeometry(0.98, 0.14, 0.16);
      slat.translate(0, y, -0.4 + i * 0.2);
      parts.push(slat);
    }
  }
  // Three stringers holding the decks apart.
  for (const z of [-0.38, 0, 0.38]) {
    const block = new THREE.BoxGeometry(0.98, 0.44, 0.14);
    block.translate(0, 0.13, z);
    parts.push(block);
  }
  // The bottom deck's runners, so the stack has a foot rather than floating.
  for (const z of [-0.38, 0, 0.38]) {
    const runner = new THREE.BoxGeometry(0.98, 0.16, 0.16);
    runner.translate(0, -0.42, z);
    parts.push(runner);
  }

  return merge(parts);
}

/**
 * A potted plant: a tapered pot with a cluster of foliage over it.
 *
 * One material covers both, which is a real limitation of the one-material-per-
 * kind rule `scene.ts` relies on (a disguise has to be the *same* object as the
 * props around it, not a lookalike). The foliage texture on a narrow tapered
 * pot still reads as a planter, and the alternative — a second draw call per
 * plant, or a second material a disguised player could be told apart by — costs
 * more than it buys.
 *
 * The foliage is a low-poly icosahedron rather than a sphere: at this size the
 * facets read as leaves and it is a third of the triangles.
 */
function plant(): THREE.BufferGeometry {
  const pot = new THREE.CylinderGeometry(0.24, 0.18, 0.3, RADIAL_SEGMENTS, 1, false);
  pot.translate(0, -0.35, 0);

  const stem = new THREE.CylinderGeometry(0.04, 0.05, 0.24, 8, 1, false);
  stem.translate(0, -0.1, 0);

  const parts: THREE.BufferGeometry[] = [pot, stem];
  // Three offset clumps rather than one ball, so it has a silhouette.
  const clumps: [number, number, number, number][] = [
    [0, 0.18, 0, 0.34],
    [-0.16, 0.34, 0.08, 0.22],
    [0.15, 0.32, -0.1, 0.2],
  ];
  for (const [x, y, z, radius] of clumps) {
    const clump = new THREE.IcosahedronGeometry(radius, 0);
    clump.translate(x, y, z);
    parts.push(clump);
  }

  return merge(parts);
}

/**
 * Concatenate parts into one geometry with one draw range.
 *
 * Written here rather than taken from `BufferGeometryUtils.mergeGeometries`,
 * and the reason is not weight. Importing `three/addons/*` makes Vite bundle a
 * second copy of Three: the dev server pre-bundles the addon and its own
 * `import ... from "three"` resolves separately, which shows up as
 * "Multiple instances of Three.js being imported" and means a geometry built by
 * one copy gets attached to an `InstancedMesh` built by the other. Everything in
 * this renderer is `instanceof`-checked (`reshapeAvatar`) or handed straight to
 * `WebGLRenderer`, so two copies is a class of bug worth not having for the
 * fifteen lines below.
 *
 * Everything is flattened to non-indexed first. `mergeGeometries` refuses a mix
 * of indexed and non-indexed inputs, and these are exactly that mix —
 * `BoxGeometry` and `CylinderGeometry` are indexed, `IcosahedronGeometry` is
 * not. At these triangle counts (a pallet is the worst at ~230) the duplicated
 * vertices are not worth an index buffer.
 *
 * The parts are disposed. They have never been uploaded to a GPU at this point,
 * so this is about the CPU-side buffers rather than VRAM, but
 * `GameRenderer.dispose` can only reach the merged result.
 */
function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const flat = parts.map((part) => part.toNonIndexed());

  let vertices = 0;
  for (const part of flat) vertices += part.getAttribute("position").count;

  const position = new Float32Array(vertices * 3);
  const normal = new Float32Array(vertices * 3);
  const uv = new Float32Array(vertices * 2);

  let written = 0;
  for (const part of flat) {
    const partPosition = part.getAttribute("position");
    position.set(partPosition.array as Float32Array, written * 3);
    normal.set(part.getAttribute("normal").array as Float32Array, written * 3);
    uv.set(part.getAttribute("uv").array as Float32Array, written * 2);
    written += partPosition.count;
  }

  const merged = new THREE.BufferGeometry();
  merged.setAttribute("position", new THREE.BufferAttribute(position, 3));
  merged.setAttribute("normal", new THREE.BufferAttribute(normal, 3));
  merged.setAttribute("uv", new THREE.BufferAttribute(uv, 2));

  // `toNonIndexed` returns `this` for a geometry that was already non-indexed,
  // so `flat` and `parts` can share entries. Disposing through a Set means such
  // a geometry is not disposed twice — harmless today, but the arrays above are
  // copies either way, so there is nothing left to keep alive.
  for (const part of new Set([...parts, ...flat])) part.dispose();
  return merged;
}
